// ─── Legal / regulatory configuration ──────────────────────────────────────
// Central place for the versioned legal documents (CGU, CGV, mentions légales,
// politique de confidentialité) and the consent-tracking helpers.
//
// ⚠️  Le CONTENU juridique de ces documents (voir src/pages/Legal/content.tsx)
//     est un PROJET rédigé par l'équipe produit. Il DOIT être relu et validé
//     par un avocat spécialisé santé/numérique et par le DPO avant toute
//     commercialisation. Les champs factuels marqués « [À COMPLÉTER] »
//     (SIREN, capital, adresse, hébergeur HDS, DPO…) doivent être renseignés.

/**
 * Bump this whenever the substance of any legal document changes. Users who
 * accepted an older version are re-prompted to accept the new one (the
 * consent modal reappears, the "accepted version" is compared against this).
 * Format: YYYY-MM-DD of the effective date.
 */
export const LEGAL_VERSION = '2026-10-29'

/**
 * When true, each document displays a visible "projet — en cours de validation
 * juridique" banner. Flip to false once the documents have been reviewed and
 * approved by legal counsel / DPO.
 */
export const LEGAL_DRAFT = true

/**
 * Editor / publisher identity — used by the "Mentions légales" page.
 * Valeurs issues des mentions légales publiées sur careflai.com (fév. 2026).
 */
export const LEGAL_ENTITY = {
  appName: 'Careflai',
  legalName: 'DEEPBLOOM',
  tradeName: 'CAREFLAI',
  legalForm: 'SASU (société par actions simplifiée unipersonnelle)',
  capital: '1 000 €',
  rcsCity: 'Lille Métropole',
  rcsNumber: '991 705 104',
  siren: '991 705 104',
  siret: '991 705 104 00016',
  vatNumber: 'FR00991705104',
  address: 'Avenue Eugène Avinée, 59120 Loos, France',
  publicationDirector: 'Idir Acherar',
  contactEmail: 'acherar@careflai.com',
  dpoEmail: 'acherar@careflai.com', // [À COMPLÉTER si un DPO dédié est désigné]
  supportEmail: 'acherar@careflai.com',
} as const

/** Hosting providers — LCEN requires naming the host; health data needs HDS. */
export const LEGAL_HOSTING = {
  appHost: 'Amazon Web Services, Inc.',
  appHostAddress: '410 Terry Avenue North, Seattle, WA 98109, États-Unis',
  registrar: 'IONOS SE',
  // AWS est certifié HDS ; les données de santé sont hébergées en régions UE.
  hdsHost: 'Amazon Web Services (régions Union européenne)',
  hdsCertification: "AWS bénéficie de la certification HDS [référence du certificat à confirmer]",
} as const

export type LegalSlug = 'cgu' | 'cgv' | 'mentions-legales' | 'confidentialite'

export interface LegalDocMeta {
  slug: LegalSlug
  /** Short label for menus/footers. */
  label: string
  /** Full document title. */
  title: string
}

export const LEGAL_DOCS: LegalDocMeta[] = [
  { slug: 'cgu', label: "Conditions d'utilisation", title: "Conditions Générales d'Utilisation (CGU)" },
  { slug: 'cgv', label: 'Conditions de vente', title: 'Conditions Générales de Vente (CGV)' },
  { slug: 'mentions-legales', label: 'Mentions légales', title: 'Mentions légales' },
  { slug: 'confidentialite', label: 'Confidentialité', title: 'Politique de confidentialité & protection des données' },
]

export function legalDocMeta(slug: string): LegalDocMeta | undefined {
  return LEGAL_DOCS.find((d) => d.slug === slug)
}

// ─── Consent tracking ───────────────────────────────────────────────────────
// Primary capture is the required checkbox at sign-up. For already-registered
// users the LegalConsentNotice modal captures it. Both persist to the server
// (uiPreferences.legalAcceptedAt / legalAcceptedVersion) so acceptance is
// provable across devices; a localStorage mirror avoids a modal flash on boot.

const LEGAL_ACCEPT_STORAGE_KEY = 'careflai.legalAccepted.v1'

/** Fast-path local cache: has this device accepted the CURRENT legal version? */
export function hasAcceptedLegalLocal(): boolean {
  try {
    return localStorage.getItem(LEGAL_ACCEPT_STORAGE_KEY) === LEGAL_VERSION
  } catch {
    return false
  }
}

/** Record local acceptance of the current version (called at sign-up + modal). */
export function recordLegalAcceptanceLocal(): void {
  try {
    localStorage.setItem(LEGAL_ACCEPT_STORAGE_KEY, LEGAL_VERSION)
  } catch {
    /* ignore — storage disabled */
  }
}

/** Authoritative check against the server-side preferences blob. */
export function hasAcceptedLegal(prefs?: { legalAcceptedVersion?: string | null } | null): boolean {
  return prefs?.legalAcceptedVersion === LEGAL_VERSION
}
